import { useMemo, useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/ThemeContext';
import { useBranding } from '../../lib/branding';
import PortalBrandMark from '../branding/PortalBrandMark';
import { HelpMenu } from '../QuickTour';
import LanguageToggle from '../LanguageToggle';
import OpsManualLink from './OpsManualLink';
import OrgSwitcher from './OrgSwitcher';
import CommandCenterButton from './CommandCenterButton';
import { SidebarPrefsControls } from './SidebarPrefs';
import DemoSessionToggle from './DemoSessionToggle';
import { useNavLayout } from '../../lib/NavLayoutContext';
import { useSurveysModuleEnabled } from '../../modules/surveys/hooks/useSurveysModuleEnabled';
import { getVisibleNavItems } from '../../lib/navItems';
import { canSeeOperationalBase } from '../../lib/obVisibility';
import { CC_VERTICAL_EVENT } from '../../lib/vertical';
import { authAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import {
    LayoutDashboard,
    CreditCard,
    Users,
    MapPin,
    BarChart3,
    Settings,
    Building2,
    LogOut,
    UserCog,
    Menu,
    X,
    ChevronDown,
    SlidersHorizontal,
    RotateCcw,
    Wifi,
    DollarSign,
    FileText,
    Mail,
    Trophy,
    Clock,
    Activity,
    Presentation,
    GitCompare,
    Moon,
    Sun,
    Tag,
    Shield,
    Link2,
    Gift,
    ClipboardList,
    Boxes,
    Sparkles,
    Eye,
    ScanSearch,
    PanelTop
} from 'lucide-react';

// Theme toggle component for sidebar
const ThemeToggleSidebar = ({ surface = 'sidebar' }) => {
    const { theme, toggleTheme } = useTheme();
    const { t } = useTranslation();
    const isDark = theme === 'dark';

    return (
        <button
            onClick={toggleTheme}
            className="sidebar-link w-full justify-between"
            data-testid={`theme-toggle-${surface}`}
        >
            <div className="flex items-center gap-3">
                {isDark ? (
                    <Sun size={20} className="text-amber-500" />
                ) : (
                    <Moon size={20} />
                )}
                <span>{isDark ? t('nav.lightMode', 'Light Mode') : t('nav.darkMode', 'Dark Mode')}</span>
            </div>
        </button>
    );
};

/* v4.30 — Calm YulOnTap recession when an organization has its own
 * portal identity. Renders nothing when the tenant is still on the
 * platform default (the sidebar's top mark already says YulOnTap in
 * that case). */
const PoweredByFootnote = () => {
    const { t } = useTranslation();
    const { loaded, payload } = useBranding();
    if (!loaded) return null;
    const hasOrg = !!(payload && payload.logo_source && payload.logo_source !== 'platform');
    if (!hasOrg) return null;
    return (
        <div
            className="mt-3 px-2 text-[10px] tracking-[0.16em] uppercase text-slate-400 dark:text-slate-500 flex items-center gap-1.5"
            data-testid="sidebar-powered-by-footnote"
        >
            <span>{t('portalIdentity.sidebar.poweredBy')}</span>
            <img
                src="/yulontap-logo.png"
                alt="YulOnTap"
                style={{ height: 12, width: 12, objectFit: 'contain', display: 'inline-block' }}
            />
            <span>YulOnTap</span>
        </div>
    );
};

const Sidebar = () => {
    const { t, i18n } = useTranslation();
    const { user, logout, isSuperAdmin, isPartner, isClientAdmin, isLocationManager, canManageLocations, hasPermission } = useAuth();
    const surveysEnabled = useSurveysModuleEnabled(user);
    const { clearSessionTheme } = useTheme();
    const { setNavLayout } = useNavLayout();
    const navigate = useNavigate();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    // Re-render when the Command Center resolves a different vertical for the
    // current scope (super admins: the scope decides whether Card Fleet Ops is
    // a standalone page or a Command Center block).
    const [ccTick, setCcTick] = useState(0);
    useEffect(() => {
        const bump = () => setCcTick((n) => n + 1);
        window.addEventListener(CC_VERTICAL_EVENT, bump);
        return () => window.removeEventListener(CC_VERTICAL_EVENT, bump);
    }, []);
    void ccTick;

    const handleLogout = () => {
        clearSessionTheme(); // Clear session-only dark mode on logout
        logout();
        navigate('/login');
    };

    const closeMobile = () => setIsMobileOpen(false);

    // Get role display info
    const getRoleInfo = () => {
        if (isSuperAdmin()) return { label: t('roles.super_admin'), color: 'text-red-600 bg-red-50' };
        if (isClientAdmin()) return { label: t('roles.client_admin'), color: 'text-blue-600 bg-blue-50' };
        if (isLocationManager()) return { label: t('roles.location_manager'), color: 'text-emerald-600 bg-emerald-50' };
        return { label: t('roles.employee'), color: 'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800' };
    };

    // Navigation items — sourced from the shared single source of
    // truth at `lib/navItems.js` so future modules are added once
    // and appear in BOTH the sidebar and the top-nav shell.
    // The shape returned here is identical to what this component
    // historically built inline.
    const getNavItems = () => {
        const authCtx = {
            user, isSuperAdmin, isPartner, isClientAdmin, isLocationManager,
            canManageLocations, hasPermission, surveysEnabled,
            activeVertical: user?.organization_vertical || null,
            // This shell renders the gradient Command Center button, so the
            // duplicate list entry is suppressed here.
            commandCenterButtonVisible: true,
        };
        return getVisibleNavItems(authCtx).map((item) => ({
            id: item.id,
            to: item.to,
            icon: item.icon,
            labelKey: item.labelKey,
            show: true, // already filtered by getVisibleNavItems
            insightId: item.insightId,
            highlight: item.highlight,
            color: item.color,
            category: item.category,
        }));
    };

    // Admin section items — sourced from `lib/navItems.js`
    // (category='admin'). Sidebar continues to render this list
    // inside the collapsible Admin block as before.
    const getAdminItems = () => {
        const authCtx = {
            user, isSuperAdmin, isPartner, isClientAdmin, isLocationManager,
            canManageLocations, hasPermission, surveysEnabled,
            activeVertical: user?.organization_vertical || null,
            // This shell renders the gradient Command Center button, so the
            // duplicate list entry is suppressed here.
            commandCenterButtonVisible: true,
        };
        // We can reuse getVisibleNavItems + filter, but to keep the
        // sidebar code path narrow we inline the filter here.
        return getVisibleNavItems(authCtx)
            .filter((i) => i.category === 'admin')
            .map((item) => ({
                id: item.id,
                to: item.to,
                icon: item.icon,
                labelKey: item.labelKey,
                show: true,
                insightId: item.insightId,
                highlight: item.highlight,
                color: item.color,
                category: item.category,
            }));
    };

    const navItems = getNavItems();
    const adminItems = getAdminItems();

    // ── P_PREFERRED_TABS_1.0 — personal sidebar visibility preference ──
    // Owned by the user record (like language/theme). Hiding a tab is
    // strictly cosmetic for THIS user: routes, permissions, features
    // and other users are untouched.
    const [hiddenTabs, setHiddenTabs] = useState(() => user?.sidebar_hidden_tabs || []);

    // Re-sync when the user record (re)hydrates — on fresh login the sidebar
    // mounts before /auth/me resolves, so the initializer alone loses the
    // saved preference. savePrefs keeps user.sidebar_hidden_tabs in sync,
    // making this effect a no-op after local saves.
    useEffect(() => {
        setHiddenTabs(user?.sidebar_hidden_tabs || []);
    }, [user?.id, JSON.stringify(user?.sidebar_hidden_tabs || [])]); // eslint-disable-line react-hooks/exhaustive-deps

    const [showAllTabs, setShowAllTabs] = useState(false); // temporary reveal
    const [customizeOpen, setCustomizeOpen] = useState(false);
    // The secondary row at the bottom starts closed, so the navigation list
    // gets the height. Nothing is remembered on purpose: an operator opens it
    // to change one thing and leaves.
    const [accountOpen, setAccountOpen] = useState(false);
    const [draftHidden, setDraftHidden] = useState([]);
    const [savingPrefs, setSavingPrefs] = useState(false);

    // EVERY function shown in the sidebar is customizable: the Operational
    // Base entry plus the complete nav list (primary, functions, general
    // and admin — `navItems` already contains all of them).
    const customizableItems = useMemo(() => ([
        { id: 'operational-base', icon: Sparkles, labelKey: 'nav.operationalBase', color: 'text-emerald-500' },
        ...navItems,
    ]), [navItems]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCustomize = () => {
        setDraftHidden([...hiddenTabs]);
        setCustomizeOpen(true);
    };

    const toggleDraft = (id) => {
        setDraftHidden((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const savePrefs = async (nextHidden) => {
        setSavingPrefs(true);
        try {
            await authAPI.updateSidebarPrefs(nextHidden);
            setHiddenTabs(nextHidden);
            if (user) user.sidebar_hidden_tabs = nextHidden; // keep auth snapshot in sync
            if (nextHidden.length === 0) setShowAllTabs(false);
            setCustomizeOpen(false);
            toast.success(t('sidebarPrefs.saved'));
        } catch {
            toast.error(t('sidebarPrefs.saveFailed'));
        } finally {
            setSavingPrefs(false);
        }
    };

    // Locale-aware alphabetical ordering of the function/action menu. Sorts
    // by the CURRENTLY DISPLAYED (translated) label so each language orders
    // by its own labels, and re-sorts when the language changes. Only the
    // ordering changes — permissions, visibility, icons, styling and the
    // pinned Operational Base entry are untouched. New/enabled functions land
    // in their correct alphabetical slot automatically (no manual per-language
    // lists). Collator handles accents (é, ñ) correctly for FR/ES.
    const navCollator = useMemo(
        () => new Intl.Collator(i18n.language || 'en', { sensitivity: 'base', numeric: true }),
        [i18n.language],
    );
    const alphaByLabel = useCallback(
        (items) => [...items].sort((a, b) => navCollator.compare(t(a.labelKey), t(b.labelKey))),
        [navCollator, t],
    );

    // Memoize the visible navigation lists — they only depend on the parent
    // `navItems` / `adminItems` references. Avoids re-running `.filter`
    // every render (sidebar re-renders on every route change).
    const visibleNavItems = useMemo(() => {
        const hidden = new Set(showAllTabs ? [] : hiddenTabs);
        // An org with Operational Base OFF must not see it anywhere, and
        // `showAllTabs` must not be able to bring it back.
        if (!canSeeOperationalBase(user)) hidden.add('operational-base');
        return alphaByLabel(navItems.filter((item) => item.show && !hidden.has(item.id)));
    }, [navItems, hiddenTabs, showAllTabs, alphaByLabel, user]);
    const visibleAdminItems = useMemo(() => {
        const hidden = new Set(showAllTabs ? [] : hiddenTabs);
        return alphaByLabel(adminItems.filter((item) => item.show && !hidden.has(item.id)));
    }, [adminItems, hiddenTabs, showAllTabs, alphaByLabel]);

    // Operational Base is an ORG-LEVEL switch, default OFF. Super admins always
    // keep access (the /auth/me flag is already true for them).
    // Org switch for clients, personal "Show Operational Base" for super
    // admins (default OFF). When hidden, the brand link goes to /dashboard.
    const operationalBaseOff = !canSeeOperationalBase(user);
    const operationalBaseHidden = operationalBaseOff
        || (!showAllTabs && hiddenTabs.includes('operational-base'));
    const brandHomePath = operationalBaseOff ? '/dashboard' : '/operational-base';

    const NavItem = ({ to, icon: Icon, labelKey, onClick, insightId }) => (
        <NavLink
            to={to}
            onClick={onClick}
            className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${labelKey.split('.').pop()}`}
            data-insight-id={insightId}
            data-insight-label={t(labelKey)}
            title={t(labelKey)}
            aria-label={t(labelKey)}
        >
            <Icon size={20} className="shrink-0" />
            <span className="truncate whitespace-nowrap">{t(labelKey)}</span>
        </NavLink>
    );

    const SidebarContent = ({ mobile = false }) => (
        <div className="flex flex-col h-full">
            {/* Logo — v4.30: dynamic portal-identity mark.
              * Falls back to YulOnTap when the tenant has no portal logo.
              * v4.34.14 — Inline layout toggle (desktop only) replaces
              * the bulky NavLayoutPicker that previously squished the
              * tab list at the bottom of the sidebar. */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                <NavLink
                    to={brandHomePath}
                    onClick={closeMobile}
                    data-testid="legacy-sidebar-logo-link"
                    className="flex items-center gap-3 group min-w-0"
                >
                    <PortalBrandMark
                        variant="sidebar"
                        fallbackToPlatform={true}
                        data-testid="sidebar-portal-brand-mark"
                    />
                </NavLink>
                <button
                    type="button"
                    onClick={() => setNavLayout('top')}
                    className="hidden lg:inline-flex shrink-0 p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                    aria-label={t('nav.layout.switchToTop')}
                    title={t('nav.layout.switchToTop')}
                    data-testid="sidebar-switch-to-top"
                >
                    <PanelTop className="w-4 h-4" />
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {/* Command Center — prominent gradient entry, always first */}
                <div className="mb-3">
                    <CommandCenterButton
                        variant="sidebar"
                        testId={mobile ? 'sidebar-mobile-command-center-btn' : 'sidebar-command-center-btn'}
                        onClick={closeMobile}
                    />
                </div>

                {/* Phase 2 — discoverability entry to the new Operational Base shell */}
                {!operationalBaseHidden && (
                <NavLink
                    to="/operational-base"
                    onClick={closeMobile}
                    data-testid="nav-operational-base"
                    className={({ isActive }) =>
                        `sidebar-link ${isActive ? 'active' : ''} mb-2 border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50/40 dark:bg-emerald-900/20`
                    }
                >
                    <Sparkles size={20} className="text-emerald-600 dark:text-emerald-400" />
                    <span className="font-medium">{t('nav.operationalBase')}</span>
                </NavLink>
                )}

                {visibleNavItems.map((item) => (
                    <NavItem 
                        key={item.to} 
                        {...item} 
                        onClick={closeMobile}
                    />
                ))}

                {/* Admin Section */}
                {(isSuperAdmin() || isPartner()) && visibleAdminItems.length > 0 && (
                    <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setIsAdminOpen(!isAdminOpen)}
                            className="sidebar-link w-full justify-between"
                            data-testid="nav-admin-toggle"
                        >
                            <div className="flex items-center gap-3">
                                <Settings size={20} />
                                <span>{t('nav.adminSection')}</span>
                            </div>
                            <ChevronDown 
                                size={16} 
                                className={`transition-transform duration-200 ${isAdminOpen ? 'rotate-180' : ''}`}
                            />
                        </button>
                        {isAdminOpen && (
                            <div className="ml-4 mt-1 space-y-1">
                                {visibleAdminItems.map((item) => (
                                    <NavItem 
                                        key={item.to} 
                                        {...item}
                                        onClick={closeMobile}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Preferred Tabs controls — the SHARED component, so this
                    shell and the Operational Base shell show the same thing
                    and write the same setting. Visible to EVERY signed-in
                    user now, partners included. */}
                <SidebarPrefsControls
                    items={customizableItems}
                    prefs={{ hiddenTabs, showAllTabs, setShowAllTabs, saving: savingPrefs, save: savePrefs }}
                />
            </nav>

            {/* User Section */}            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                {/* Organization/Location Badge (for non-super admins) */}
                {!isSuperAdmin() && (user?.organization_name || user?.location_name) && (
                    <div className="mb-3 px-2">
                        {user?.organization_name && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 mb-1">
                                <Building2 size={12} />
                                <span className="truncate">{user.organization_name}</span>
                            </div>
                        )}
                        {user?.location_name && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-500">
                                <MapPin size={12} />
                                <span className="truncate">{user.location_name}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* v4.33.0 — Multi-organization switcher. Renders itself
                    null when the user has ≤ 1 assigned organization. */}
                <OrgSwitcher />
                
                <div className="flex items-center gap-3 mb-4 px-2">
                    <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {user?.name || 'User'}
                        </p>
                        {!isPartner() && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleInfo().color}`}>
                            {getRoleInfo().label}
                        </span>
                        )}
                    </div>
                </div>
                
                {/* Everything secondary folds into ONE row, so the navigation
                    list above keeps the height. Collapsed by default; tapping
                    the row expands it. Relocation only — every control inside
                    is the same component, with the same actions and state. */}
                <button
                    type="button"
                    onClick={() => setAccountOpen((v) => !v)}
                    className="sidebar-link w-full justify-between"
                    aria-expanded={accountOpen}
                    data-testid="sidebar-account-row"
                >
                    <span className="flex items-center gap-3">
                        <Settings size={20} />
                        <span>{t('nav.account', 'Account')}</span>
                    </span>
                    <ChevronDown size={16}
                                 className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
                </button>

                {accountOpen && (
                    <div className="mt-1 mb-2 max-h-[45vh] overflow-y-auto space-y-2 pr-1"
                         data-testid="sidebar-account-panel">
                        {/* Theme Toggle */}
                        <div className="px-2">
                            <ThemeToggleSidebar surface={mobile ? 'sidebar-mobile' : 'sidebar'} />
                        </div>

                        {/* Language Toggle */}
                        <div className="px-2">
                            <LanguageToggle
                                variant="compact"
                                surface={mobile ? 'sidebar-mobile' : 'sidebar'}
                            />
                        </div>

                        {/* Operations Manual */}
                        <OpsManualLink />

                        {/* Help Menu */}
                        <HelpMenu />

                        {/* Personal preferences — Daily Review + (super admin)
                            Show Operational Base */}
                        <NavLink
                            to="/my-settings"
                            onClick={closeMobile}
                            className={({ isActive }) => `sidebar-link w-full ${isActive ? 'active' : ''}`}
                            data-testid="nav-my-settings"
                        >
                            <UserCog size={20} />
                            <span>{t('nav.mySettings')}</span>
                        </NavLink>

                        {/* Demo Setting — super admin only; renders null for
                            everyone else. */}
                        <DemoSessionToggle view="controls" />
                    </div>
                )}

                {/* The "Demo mode on" pill stays out here, so it can never be
                    hidden behind a collapsed row. */}
                <DemoSessionToggle view="pill" />

                <button
                    onClick={handleLogout}
                    className="sidebar-link w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700"
                    data-testid="logout-btn"
                >
                    <LogOut size={20} />
                    <span>{t('nav.logout')}</span>
                </button>
                <PoweredByFootnote />
            </div>
        </div>
    );

    return (
        <>

            {/* Mobile Header */}
            <div
                className="lg:hidden fixed left-0 right-0 h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 z-40 flex items-center justify-between px-4"
                style={{ top: 'var(--preview-offset, 0px)' }}
            >
                <PortalBrandMark
                    variant="topbar"
                    fallbackToPlatform={true}
                    data-testid="mobile-header-portal-brand-mark"
                />
                <div className="flex items-center gap-2">
                    <CommandCenterButton variant="topnav" testId="mobile-header-command-center-btn" />
                    <button
                        onClick={() => setIsMobileOpen(true)}
                        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                        data-testid="mobile-menu-btn"
                    >
                        <Menu size={24} />
                    </button>
                </div>
            </div>

            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-slate-900/50 z-40"
                    onClick={closeMobile}
                />
            )}

            {/* Mobile Sidebar */}
            <aside className={`
                lg:hidden fixed top-0 left-0 h-full w-72 bg-white dark:bg-slate-800 z-50
                transform transition-transform duration-300 ease-in-out
                ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <button
                    onClick={closeMobile}
                    className="absolute top-4 right-4 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    data-testid="mobile-menu-close"
                >
                    <X size={20} />
                </button>
                <SidebarContent mobile={true} />
            </aside>

            {/* Desktop Sidebar */}
            <aside
                className="hidden lg:block fixed left-0 h-full w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 z-30"
                style={{ top: 'var(--preview-offset, 0px)' }}
            >
                <SidebarContent mobile={false} />
            </aside>
        </>
    );
};

export default Sidebar;
